// Rift Moth (replaces `wisp`) — the only flying family (P1 §2/09, elite §4: Prism Wing).
// Small insectoid: dark segmented thorax, two broad faceted slate-glass wings with magenta eye spots and a bright
// crystalline abdomen. Hovers (hover 1.4 m) with an unmistakable wing beat driven by `flap`.
// Wind-up: the crystal abdomen curls under for the dive and the head dips.
import * as THREE from 'three/webgpu';
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL } from './kit';
import { facetWing, mirrorX, shard, wingBand } from './parts_swarm';

type V3 = [number, number, number];
const BODY_Y = 0.36;
const WING_IN = PAL.slate, WING_GLASS = 0xb0c0dc, WING_EDGE = PAL.iron, CRYSTAL = 0xff9ad6;

/** two-tone faceted wing: dark slate inner fan + pale glass outer band (k = inner ring fraction) */
function wing(root: V3, rim: V3[], wx: number, pleat: number, k = 0.55): { inner: THREE.BufferGeometry; band: THREE.BufferGeometry } {
  const r = rim.map(([x, y, z]) => [root[0] + (x - root[0]) * wx, y, z] as V3);
  const mid = r.map(([x, y, z], i) => [root[0] + (x - root[0]) * k, root[1] + (y - root[1]) * k + (i % 2 ? pleat * 0.5 : 0), root[2] + (z - root[2]) * k] as V3);
  return { inner: facetWing(root, mid, 0, 0.014), band: wingBand(mid, r, pleat, 0.014) };
}

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();
  const wx = elite ? 1.3 : 1;

  // ---- thorax (segmented) and head
  b.add(at(new THREE.SphereGeometry(1, 10, 8), 0, BODY_Y, 0.06, 0, 0, 0, [0.12, 0.11, 0.14]), { color: PAL.iron });
  b.add(at(new THREE.SphereGeometry(1, 8, 6), 0, BODY_Y - 0.01, -0.09, 0, 0, 0, [0.1, 0.095, 0.09]), { color: PAL.basaltLight });
  b.add(at(new THREE.SphereGeometry(0.075, 8, 6), 0, BODY_Y + 0.01, 0.23), { color: PAL.iron, pivot: [0, BODY_Y, 0.16], raise: 0.35 });
  for (const s of [-1, 1]) {
    b.add(at(new THREE.SphereGeometry(0.035, 6, 4), s * 0.05, BODY_Y + 0.03, 0.28), { color: PAL.magenta, glow: true, pivot: [0, BODY_Y, 0.16], raise: 0.35 });
    b.add(limb([s * 0.03, BODY_Y + 0.07, 0.27], [s * 0.14, BODY_Y + 0.26, 0.42], 0.012, 0.008, 4), { color: PAL.ironLight, pivot: [0, BODY_Y, 0.16], raise: 0.35 }); // antenna
    b.add(at(new THREE.ConeGeometry(0.035, 0.12, 4), s * 0.15, BODY_Y + 0.27, 0.43, 0.6, 0, -s * 0.5), { color: PAL.ironLight, pivot: [0, BODY_Y, 0.16], raise: 0.35 }); // feathered tip
    // two pairs of dangling legs
    for (const z of [0.1, -0.04]) b.add(limb([s * 0.05, BODY_Y - 0.07, z], [s * 0.1, BODY_Y - 0.17, z + 0.06], 0.01, 0.007, 4), { color: PAL.ironLight });
  }
  if (elite) {
    // gold-edged crystalline thorax: gold collar and two crystal crests
    b.add(at(new THREE.TorusGeometry(0.115, 0.022, 4, 10), 0, BODY_Y + 0.01, 0.0, 0, 0, 0, [1, 1, 1]), { color: PAL.gold });
    b.add(shard([0, BODY_Y + 0.09, 0.08], [0, BODY_Y + 0.27, -0.02], 0.045, 5), { color: CRYSTAL });
    b.add(shard([0, BODY_Y + 0.09, -0.06], [0, BODY_Y + 0.2, -0.16], 0.035, 5), { color: PAL.gold });
  }

  // ---- crystalline abdomen (curls under on wind-up)
  const abd = { pivot: [0, BODY_Y, -0.12] as V3, raise: -0.8 };
  b.add(shard([0, BODY_Y - 0.01, -0.12], [0, BODY_Y - 0.12, elite ? -0.68 : -0.6], elite ? 0.12 : 0.1, 6), { color: CRYSTAL, ...abd });
  b.add(shard([0, BODY_Y - 0.03, -0.2], [0, BODY_Y - 0.11, -0.52], 0.05, 4), { color: PAL.magenta, glow: true, ...abd });

  // ---- wings: broad faceted glass, resting slightly down, flapping up (flap sign = side)
  const root: V3 = [0.07, BODY_Y + 0.05, 0.03];
  const fore: V3[] = [[0.14, BODY_Y + 0.07, 0.2], [0.42, BODY_Y + 0.04, 0.27], [0.68, BODY_Y - 0.04, 0.18], [0.8, BODY_Y - 0.1, 0.0], [0.7, BODY_Y - 0.1, -0.2], [0.48, BODY_Y - 0.06, -0.36], [0.24, BODY_Y - 0.02, -0.4], [0.1, BODY_Y + 0.02, -0.2]];
  const eye: V3 = [0.07 + 0.5 * wx, BODY_Y - 0.03, -0.04];
  for (const s of [-1, 1]) {
    const w = { pivot: [s * root[0], root[1], root[2]] as V3, flap: s * 1.15 };
    const g = wing(root, fore, wx, 0.04), m = (x: THREE.BufferGeometry) => (s > 0 ? x : mirrorX(x));
    b.add(m(g.inner), { color: WING_IN, ...w });
    b.add(m(g.band), { color: WING_GLASS, ...w });
    b.add(m(limb([root[0], root[1] + 0.01, root[2] + 0.12], [0.42 * wx + 0.07, BODY_Y + 0.05, 0.27], 0.02, 0.012, 4)), { color: WING_EDGE, ...w }); // leading-edge vein
    b.add(at(new THREE.OctahedronGeometry(0.06), s * eye[0], eye[1] + 0.02, eye[2], 0, 0, 0, [1, 0.35, 1.4]), { color: PAL.magenta, glow: true, ...w }); // eye spot
    if (elite) {
      // Prism Wing: gold leading edge and a second, smaller hind-wing pair
      b.add(m(limb([0.42 * wx + 0.07, BODY_Y + 0.05, 0.27], [0.68 * wx + 0.07 * (1 - wx), BODY_Y - 0.03, 0.18], 0.016, 0.01, 4)), { color: PAL.gold, ...w });
      const hind: V3[] = [[0.1, BODY_Y - 0.02, -0.16], [0.36, BODY_Y - 0.1, -0.3], [0.52, BODY_Y - 0.16, -0.48], [0.3, BODY_Y - 0.1, -0.58], [0.08, BODY_Y - 0.04, -0.3]];
      const hw = wing([0.06, BODY_Y, -0.08], hind, 1.15, 0.03, 0.5), hp = { pivot: [s * 0.06, BODY_Y, -0.08] as V3, flap: s * 0.9 };
      b.add(m(hw.inner), { color: WING_IN, ...hp });
      b.add(m(hw.band), { color: 0xd6b0e8, ...hp });
    }
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Rift Moth', build, stride: 1.9, bob: 0.04, hover: 1.4, outline: 0.025 };
